import { useEffect, useMemo, useState } from 'react';
import {
  admobDaily,
  admobMediationDaily,
  hourlyStats,
  lanDaily,
  listApps,
  type App as AppInfo,
  type HourlyStatsResp,
  type LanDailyResp,
} from '../api';

const NET_COLORS: Record<string, string> = {
  line: '#7ED321',
  applovin: '#F5A623',
  admob: '#4A90E2',
};
const PALETTE = [
  '#008FFB',
  '#00E396',
  '#FEB019',
  '#FF4560',
  '#775DD0',
  '#3F51B5',
  '#546E7A',
  '#D4526E',
  '#8D5B4C',
  '#F86624',
  '#26C6DA',
  '#EF5350',
];
const netColorMap = new Map<string, string>();
function netColor(name: string): string {
  const key = name.toLowerCase();
  if (NET_COLORS[key]) return NET_COLORS[key];
  const cached = netColorMap.get(key);
  if (cached) return cached;
  const c = PALETTE[netColorMap.size % PALETTE.length];
  netColorMap.set(key, c);
  return c;
}

const brandColorMap = new Map<string, string>();
function brandColor(name: string): string {
  const cached = brandColorMap.get(name);
  if (cached) return cached;
  const c = PALETTE[brandColorMap.size % PALETTE.length];
  brandColorMap.set(name, c);
  return c;
}

/** "yyyymmddHH" → "HH" 表示 */
function shortHour(h: string): string {
  return h.slice(-2);
}

export default function Summary() {
  const [data, setData] = useState<HourlyStatsResp | null>(null);
  const [lan, setLan] = useState<LanDailyResp | null>(null);
  const [admob, setAdmob] = useState<LanDailyResp | null>(null);
  const [med, setMed] = useState<(LanDailyResp & { brands?: string[] }) | null>(null);
  const [medBrand, setMedBrand] = useState('');
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [h, l, m, a] = await Promise.all([
        hourlyStats(24),
        lanDaily(30),
        admobDaily(30),
        listApps(),
      ]);
      setData(h);
      setLan(l);
      setAdmob(m);
      setApps(a.apps);
    } catch (e) {
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    const iv = setInterval(() => void load(), 60_000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    let alive = true;
    admobMediationDaily(30, medBrand)
      .then((r) => alive && setMed(r))
      .catch((e) => alive && setErr(String(e)));
    return () => {
      alive = false;
    };
  }, [medBrand]);

  const brandOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of apps) m.set(a.package, a.brand || '(未分類)');
    return m;
  }, [apps]);

  return (
    <div>
      <h2>サマリー (直近 24 時間)</h2>
      <div className="row" style={{ gap: 8, marginBottom: 12 }}>
        <button className="ghost small" onClick={() => void load()}>
          リロード
        </button>
        {loading && <span className="muted">読み込み中…</span>}
        {err && <span style={{ color: 'var(--bad)' }}>{err}</span>}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px' }}>時間別 回転数 (広告先積み上げ)</h3>
        <HourlyStackedChart data={data} />
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px' }}>LINE 日次集計 (直近 30 日 / ブランド積み上げ)</h3>
        <LanDailyBarChart data={lan} />
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px' }}>LINE eCPM (直近 30 日 / ブランド別)</h3>
        <LanEcpmLineChart data={lan} />
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px' }}>AdMob eCPM (直近 30 日 / ブランド別)</h3>
        <LanEcpmLineChart data={admob} />
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 8px' }}>AdMob 日次集計 (直近 30 日 / ブランド積み上げ)</h3>
        <LanDailyBarChart data={admob} />
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>AdMob メディエーション (直近 30 日 / 広告ソース別)</h3>
          <select value={medBrand} onChange={(e) => setMedBrand(e.target.value)}>
            <option value="">全ブランド</option>
            {(med?.brands ?? []).map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <h4 style={{ margin: '8px 0' }}>ソース別 eCPM</h4>
        <LanEcpmLineChart data={med} />
        <h4 style={{ margin: '16px 0 8px' }}>ソース別 積み上げ</h4>
        <LanDailyBarChart data={med} />
      </div>

      <div className="card">
        <h3 style={{ margin: '0 0 8px' }}>アプリ別 24h 回転数 (ブランドで束ねる)</h3>
        <BrandAppDonut data={data} brandOf={brandOf} />
      </div>
    </div>
  );
}

/* ---------------- Chart 1: hourly stacked by network ---------------- */

function HourlyStackedChart({ data }: { data: HourlyStatsResp | null }) {
  if (!data || data.items.length === 0) {
    return <p className="muted">データがありません</p>;
  }
  const hours = data.hours;
  // hour → network → count
  const grid = new Map<string, Map<string, number>>();
  for (const h of hours) grid.set(h, new Map());
  for (const it of data.items) {
    const m = grid.get(it.hour);
    if (!m) continue;
    m.set(it.network, (m.get(it.network) || 0) + it.count);
  }
  const networks = new Set<string>();
  for (const it of data.items) networks.add(it.network);
  const netList = [...networks].sort();
  const maxTotal = Math.max(
    1,
    ...[...grid.values()].map((m) =>
      [...m.values()].reduce((a, b) => a + b, 0),
    ),
  );

  const W = 800,
    H = 260,
    padL = 40,
    padR = 12,
    padT = 12,
    padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const barW = (chartW / hours.length) * 0.75;
  const gap = chartW / hours.length - barW;

  return (
    <div>
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMinYMin meet">
          <YAxis maxVal={maxTotal} padL={padL} padT={padT} chartH={chartH} />
          {hours.map((h, i) => {
            const m = grid.get(h)!;
            let yCursor = padT + chartH;
            const x = padL + i * (barW + gap) + gap / 2;
            return (
              <g key={h}>
                {netList.map((n) => {
                  const c = m.get(n) || 0;
                  if (c === 0) return null;
                  const barH = (c / maxTotal) * chartH;
                  yCursor -= barH;
                  return (
                    <rect
                      key={n}
                      x={x}
                      y={yCursor}
                      width={barW}
                      height={barH}
                      fill={netColor(n)}
                    >
                      <title>{`${h.slice(-2)}時 ${n}: ${c}`}</title>
                    </rect>
                  );
                })}
                {i % 2 === 0 && (
                  <text
                    x={x + barW / 2}
                    y={H - padB + 14}
                    fill="var(--muted)"
                    fontSize={10}
                    textAnchor="middle"
                  >
                    {shortHour(h)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          fontSize: 12,
          marginTop: 6,
        }}
      >
        {netList.map((n) => (
          <span
            key={n}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span
              style={{
                width: 14,
                height: 8,
                background: netColor(n),
                borderRadius: 2,
              }}
            />
            <span>{n}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Chart 2: LAN daily stacked (imp / revenue toggle) ---------------- */

function LanDailyBarChart({ data }: { data: LanDailyResp | null }) {
  const [metric, setMetric] = useState<'imp' | 'revenue'>('imp');
  if (!data || data.items.length === 0) {
    return <p className="muted">データがありません (LAN fetch がまだ実行されていない可能性)</p>;
  }
  const dates = data.dates;
  // date → brand → value
  const grid = new Map<string, Map<string, number>>();
  for (const d of dates) grid.set(d, new Map());
  for (const it of data.items) {
    const m = grid.get(it.date);
    if (!m) continue;
    const v = metric === 'imp' ? it.imp : it.revenue;
    m.set(it.brand, (m.get(it.brand) || 0) + v);
  }
  const brandSet = new Set<string>();
  for (const it of data.items) brandSet.add(it.brand);
  const brandList = [...brandSet].sort();
  const maxTotal = Math.max(
    1,
    ...[...grid.values()].map((m) =>
      [...m.values()].reduce((a, b) => a + b, 0),
    ),
  );

  const W = 800,
    H = 260,
    padL = 50,
    padR = 12,
    padT = 12,
    padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const barW = (chartW / dates.length) * 0.8;
  const gap = chartW / dates.length - barW;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button
          className={metric === 'imp' ? '' : 'ghost small'}
          onClick={() => setMetric('imp')}
        >
          回転数
        </button>
        <button
          className={metric === 'revenue' ? '' : 'ghost small'}
          onClick={() => setMetric('revenue')}
        >
          売上 (円)
        </button>
      </div>
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMinYMin meet">
          <YAxis maxVal={maxTotal} padL={padL} padT={padT} chartH={chartH} />
          {dates.map((d, i) => {
            const m = grid.get(d)!;
            let yCursor = padT + chartH;
            const x = padL + i * (barW + gap) + gap / 2;
            return (
              <g key={d}>
                {brandList.map((b) => {
                  const v = m.get(b) || 0;
                  if (v === 0) return null;
                  const barH = (v / maxTotal) * chartH;
                  yCursor -= barH;
                  return (
                    <rect
                      key={b}
                      x={x}
                      y={yCursor}
                      width={barW}
                      height={barH}
                      fill={brandColor(b)}
                    >
                      <title>{`${shortDate(d)} ${b} ${metric === 'imp' ? '回転' : '売上'}: ${v.toLocaleString()}`}</title>
                    </rect>
                  );
                })}
                {(dates.length <= 15 || i % 5 === 0) && (
                  <text
                    x={x + barW / 2}
                    y={H - padB + 14}
                    fill="var(--muted)"
                    fontSize={9}
                    textAnchor="middle"
                  >
                    {shortDate(d)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          fontSize: 12,
          marginTop: 6,
        }}
      >
        {brandList.map((b) => (
          <span
            key={b}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span
              style={{
                width: 14,
                height: 8,
                background: brandColor(b),
                borderRadius: 2,
              }}
            />
            <span>{b}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Chart: LAN daily eCPM line by brand ---------------- */

function LanEcpmLineChart({ data }: { data: LanDailyResp | null }) {
  if (!data || data.items.length === 0) {
    return <p className="muted">データがありません</p>;
  }
  const dates = data.dates;
  // date → brand → (imp, revenue) — ecpm = revenue / imp * 1000
  const grid = new Map<string, Map<string, { imp: number; revenue: number }>>();
  for (const d of dates) grid.set(d, new Map());
  for (const it of data.items) {
    const m = grid.get(it.date);
    if (!m) continue;
    const cur = m.get(it.brand) ?? { imp: 0, revenue: 0 };
    cur.imp += it.imp;
    cur.revenue += it.revenue;
    m.set(it.brand, cur);
  }
  const brandSet = new Set<string>();
  for (const it of data.items) brandSet.add(it.brand);
  const brandList = [...brandSet].sort();

  // ブランド別 eCPM 系列
  const series = brandList.map((b) => {
    const points = dates.map((d, i) => {
      const rc = grid.get(d)?.get(b);
      const ecpm =
        rc && rc.imp > 0 ? (rc.revenue / rc.imp) * 1000 : null;
      return { i, ecpm };
    });
    return { brand: b, points };
  });
  const allValues = series.flatMap((s) =>
    s.points.map((p) => p.ecpm).filter((v): v is number => v != null),
  );
  const maxVal = Math.max(1, ...allValues);

  const W = 800,
    H = 240,
    padL = 50,
    padR = 12,
    padT = 12,
    padB = 40;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const stepX = dates.length > 1 ? chartW / (dates.length - 1) : 0;

  return (
    <div>
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMinYMin meet">
          <YAxis maxVal={maxVal} padL={padL} padT={padT} chartH={chartH} />
          {series.map((s) => {
            const pts = s.points.filter((p) => p.ecpm != null) as Array<{
              i: number;
              ecpm: number;
            }>;
            if (pts.length === 0) return null;
            const d = pts
              .map((p, idx) => {
                const x = padL + p.i * stepX;
                const y = padT + chartH - (p.ecpm / maxVal) * chartH;
                return `${idx === 0 ? 'M' : 'L'} ${x} ${y}`;
              })
              .join(' ');
            return (
              <g key={s.brand}>
                <path
                  d={d}
                  fill="none"
                  stroke={brandColor(s.brand)}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {pts.map((p) => {
                  const x = padL + p.i * stepX;
                  const y = padT + chartH - (p.ecpm / maxVal) * chartH;
                  return (
                    <circle
                      key={p.i}
                      cx={x}
                      cy={y}
                      r={2.5}
                      fill={brandColor(s.brand)}
                    >
                      <title>
                        {`${shortDate(dates[p.i])} ${s.brand}: ¥${p.ecpm.toFixed(2)}`}
                      </title>
                    </circle>
                  );
                })}
              </g>
            );
          })}
          {dates.map(
            (d, i) =>
              (dates.length <= 15 || i % 5 === 0) && (
                <text
                  key={d}
                  x={padL + i * stepX}
                  y={H - padB + 14}
                  fill="var(--muted)"
                  fontSize={9}
                  textAnchor="middle"
                >
                  {shortDate(d)}
                </text>
              ),
          )}
        </svg>
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          fontSize: 12,
          marginTop: 6,
        }}
      >
        {brandList.map((b) => (
          <span
            key={b}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span
              style={{
                width: 14,
                height: 3,
                background: brandColor(b),
              }}
            />
            <span>{b}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function shortDate(yyyymmdd: string): string {
  // "20260714" → "07-14"
  return `${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`;
}

function YAxis({
  maxVal,
  padL,
  padT,
  chartH,
}: {
  maxVal: number;
  padL: number;
  padT: number;
  chartH: number;
}) {
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  return (
    <g>
      {ticks.map((t) => {
        const y = padT + chartH - t * chartH;
        return (
          <g key={t}>
            <line
              x1={padL}
              x2={800 - 12}
              y1={y}
              y2={y}
              stroke="var(--border)"
              strokeDasharray="2 3"
            />
            <text
              x={padL - 6}
              y={y + 3}
              fill="var(--muted)"
              fontSize={10}
              textAnchor="end"
            >
              {Math.round(maxVal * t)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/* ---------------- Chart 3: brand outer / app inner donut ---------------- */

function BrandAppDonut({
  data,
  brandOf,
}: {
  data: HourlyStatsResp | null;
  brandOf: Map<string, string>;
}) {
  if (!data || data.items.length === 0) {
    return <p className="muted">データがありません</p>;
  }
  // pkg → total
  const pkgTotal = new Map<string, number>();
  for (const it of data.items) {
    if (!it.appPackage) continue;
    pkgTotal.set(it.appPackage, (pkgTotal.get(it.appPackage) || 0) + it.count);
  }
  // brand → { total, pkgs: [{pkg, count}] }
  const byBrand = new Map<
    string,
    { total: number; pkgs: Array<{ pkg: string; count: number }> }
  >();
  for (const [pkg, c] of pkgTotal.entries()) {
    const b = brandOf.get(pkg) || '(未分類)';
    const cur = byBrand.get(b) || { total: 0, pkgs: [] };
    cur.total += c;
    cur.pkgs.push({ pkg, count: c });
    byBrand.set(b, cur);
  }
  const grandTotal = [...byBrand.values()].reduce((s, b) => s + b.total, 0);
  if (grandTotal === 0) {
    return <p className="muted">データがありません</p>;
  }

  const brandList = [...byBrand.entries()].sort(
    ([, a], [, b]) => b.total - a.total,
  );

  // ジオメトリ
  const cx = 200;
  const cy = 200;
  const rInnerHole = 55;
  const rInnerOuter = 105;
  const rOuterInner = 115;
  const rOuterOuter = 165;

  let angle = -Math.PI / 2;

  const innerArcs: Array<{
    pkg: string;
    count: number;
    color: string;
    a0: number;
    a1: number;
  }> = [];
  const outerArcs: Array<{
    brand: string;
    total: number;
    color: string;
    a0: number;
    a1: number;
  }> = [];

  for (const [brand, info] of brandList) {
    info.pkgs.sort((a, b) => b.count - a.count);
    const brandStart = angle;
    for (const p of info.pkgs) {
      const span = (p.count / grandTotal) * Math.PI * 2;
      innerArcs.push({
        pkg: p.pkg,
        count: p.count,
        color: brandColor(brand), // 同ブランドは色統一 (濃さは変えない)
        a0: angle,
        a1: angle + span,
      });
      angle += span;
    }
    outerArcs.push({
      brand,
      total: info.total,
      color: brandColor(brand),
      a0: brandStart,
      a1: angle,
    });
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 16,
        alignItems: 'center',
      }}
    >
      <div style={{ width: '100%', overflow: 'hidden' }}>
        <svg viewBox={`0 0 400 400`} width="100%" preserveAspectRatio="xMidYMid meet">
          {/* outer: brand */}
          {outerArcs.map((a) => (
            <path
              key={`b-${a.brand}`}
              d={ringSlicePath(cx, cy, rOuterInner, rOuterOuter, a.a0, a.a1)}
              fill={a.color}
              stroke="var(--bg)"
              strokeWidth={1}
            >
              <title>{`${a.brand}: ${a.total} (${((a.total / grandTotal) * 100).toFixed(1)}%)`}</title>
            </path>
          ))}
          {/* inner: app */}
          {innerArcs.map((a) => (
            <path
              key={`a-${a.pkg}`}
              d={ringSlicePath(cx, cy, rInnerHole, rInnerOuter, a.a0, a.a1)}
              fill={a.color}
              fillOpacity={0.7}
              stroke="var(--bg)"
              strokeWidth={0.5}
            >
              <title>{`${a.pkg}: ${a.count} (${((a.count / grandTotal) * 100).toFixed(1)}%)`}</title>
            </path>
          ))}
          <text
            x={cx}
            y={cy - 6}
            fill="var(--text)"
            fontSize={14}
            fontWeight={700}
            textAnchor="middle"
          >
            {grandTotal}
          </text>
          <text
            x={cx}
            y={cy + 14}
            fill="var(--muted)"
            fontSize={10}
            textAnchor="middle"
          >
            24h total
          </text>
        </svg>
      </div>
      <div style={{ fontSize: 12, overflow: 'auto', maxHeight: 340 }}>
        {brandList.map(([brand, info]) => {
          const brandPct = (info.total / grandTotal) * 100;
          return (
            <div key={brand} style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 700,
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    background: brandColor(brand),
                    borderRadius: 2,
                  }}
                />
                <span>{brand}</span>
                <span className="muted">
                  {info.total} ({brandPct.toFixed(1)}%)
                </span>
              </div>
              <div style={{ marginLeft: 20, marginTop: 4 }}>
                {info.pkgs.map((p) => (
                  <div
                    key={p.pkg}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      color: 'var(--muted)',
                      fontSize: 11,
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={p.pkg}
                    >
                      {p.pkg}
                    </span>
                    <span style={{ color: 'var(--text)' }}>{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function polar(cx: number, cy: number, r: number, a: number) {
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function ringSlicePath(
  cx: number,
  cy: number,
  rInner: number,
  rOuter: number,
  a0: number,
  a1: number,
): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p0 = polar(cx, cy, rOuter, a0);
  const p1 = polar(cx, cy, rOuter, a1);
  const p2 = polar(cx, cy, rInner, a1);
  const p3 = polar(cx, cy, rInner, a0);
  return [
    `M ${p0.x} ${p0.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${p1.x} ${p1.y}`,
    `L ${p2.x} ${p2.y}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${p3.x} ${p3.y}`,
    `Z`,
  ].join(' ');
}
